import type {
  Baseline,
  IElement,
  IProductElement,
  OneOfChildElements,
  OneOfElements,
  OneOfListElements,
} from '../models/project.model';
import { IProduct, ProductRecord } from '../models/product.model';
import { ElementCategoryID } from '../models/element-categories.model';
import {
  getElementAndQuantityProperties,
  hasCount,
  isElementPropertyListEqual,
} from './element-property.helpers';
import { isEqualExpressionValues } from './expression-solving.helpers';
import {
  flattenElements,
  getChildElementsOfVersions,
  getElementById,
  getPathToElement,
  isBuildingVersionElement,
  isElement,
  isProductListCategoryGroup,
  isProductElement,
  isProductListGroup,
  isProductListItem,
  OneOfSearchableElements,
  hasChildren,
} from './recursive-element.helpers';
import { ElementPropertySource } from '../models/element-property.model';
import {
  getElementCategory,
  isElementCategoryID,
  isProductCategory,
} from './element-category.helpers';
import { isNameMatch, makeSentence } from './string.helpers';
import { getElementCategoryById } from '../templates/categories';
import { genericProductsLookup } from '../generic-products';
import { isDefined } from './array.helpers';
import { Replace } from '../models/type-helpers.model';
import { reusedContentProductId } from '../templates/categories/reused-content-property.model';
import { TMP_ELEMENT_ID } from '../constants';
import { isProjectFolder } from './project-folder.helpers';
import { OneOfFactoryElements } from '../models/factory-element.model';
import { getProductName } from './product.helpers';

/**
 * Get element names as a sentence
 * @example getElementNames(version, 'element1', 'element2') => 'element1 and element2'
 * @param version
 * @param elementsOrIds
 * @returns
 */
export const getElementNames = (
  version: Baseline,
  ...elementsOrIds: (OneOfElements | string)[]
): string => {
  const productLookup = version.products;
  const elements = elementsOrIds.map((elementOrId) => {
    if (typeof elementOrId === 'string') {
      return getElementById(version, elementOrId);
    }
    return elementOrId;
  });

  const names: string[] = elements.map((e) => getElementName(e, productLookup));

  return makeSentence(...names);
};

/**
 * Get name of an element. If the element is a productElement, the name of the product is returned.
 * @param element
 * @param productLookup Typically version.products
 * @param defaultName If no name exist, use this
 * @returns
 */
export const getElementName = <
  T extends OneOfListElements | OneOfFactoryElements | IProduct,
>(
  element: T | undefined,
  productLookup: ProductRecord = genericProductsLookup,
  defaultName = 'Unknown',
): string => {
  if (!element) {
    return defaultName;
  }
  // Support anything with a name property (Project, Product etc)
  if ('name' in element && element.name) {
    return element.name;
  }

  if (isProductElement(element) || isProductListItem(element)) {
    return getProductName(element.product_id, productLookup, defaultName);
  }
  if (isProductListCategoryGroup(element)) {
    return getElementCategoryById(element.id).name;
  }
  if (isProductListGroup(element)) {
    const productId = element.product_id ?? element.id;
    return getProductName(productId, productLookup, defaultName);
  }
  if (isElement(element)) {
    return (element.name || element.fallbackName) ?? defaultName;
  }
  return 'name' in element && element.name ? element.name : defaultName;
};

/**
 * Whether the element is automatically managed (currently equivalent to being a product element).
 *
 * All {@link IProductElement} instances are produced by element category templates or recipe
 * generation, so they are treated as non-editable structural children of their parent element.
 * Plain {@link IElement} children are never considered generated.
 *
 * @param element
 * @returns
 */
export const isGenerated = (element: OneOfListElements | undefined): boolean =>
  isProductElement(element);

/**
 *
 * @param element
 * @returns
 */
export const shouldHideProductElement = (
  element: OneOfListElements | undefined,
): boolean => {
  if (isProductElement(element)) {
    const { count, hide_product_without_count, product_id } = element;

    if (
      hide_product_without_count ??
      // 2024-11-15: Older reused content elements didn't have hideProductWithoutCount so make sure to hide reussed anyways
      product_id === reusedContentProductId
    ) {
      return !count.resolved;
    }
  }
  return false;
};

export const isMaintenanceElement = (
  element: OneOfListElements | undefined,
): element is Replace<
  IElement,
  { category_id: ElementCategoryID.Maintenance }
> =>
  isElement(element) && element.category_id === ElementCategoryID.Maintenance;

/**
 * If the children is controlled by the element category and should not be editable
 * @param element
 * @returns
 */
export const isElementWithGeneratedChildren = (
  element: OneOfListElements | undefined,
): boolean => !!getElementCategory(element)?.getChildElements;

export const getElementCategoryId = (
  elementOrId:
    OneOfElements | OneOfFactoryElements | ElementCategoryID | undefined,
): ElementCategoryID | undefined => {
  // If the elementOrId is a category ID, return it
  if (typeof elementOrId === 'string') {
    return isElementCategoryID(elementOrId) ? elementOrId : undefined;
  }

  return elementOrId && ElementPropertySource.Category in elementOrId
    ? elementOrId[ElementPropertySource.Category]
    : undefined;
};

/**
 * Whether a new system element may be nested under `parent`.
 *
 * Product rows, product categories, and categories that generate product slots
 * cannot contain a system element. Nesting one is removed on enrich, so the
 * pre-generated id is missing afterwards (DEV-3972).
 *
 * A missing parent and a version root can host system elements.
 */
export const canNestSystemElement = (
  parent: OneOfElements | undefined,
): boolean => {
  // No row, or the version itself, is a valid container for system elements.
  if (!parent || isBuildingVersionElement(parent)) return true;
  // Product rows have no child list. An add under one is dropped.
  if (isProductElement(parent)) return false;
  if (isProductCategory(getElementCategoryId(parent))) return false;
  if (isElementWithGeneratedChildren(parent)) return false;
  return true;
};

export type FocusViewSystemElementAddOptions = {
  isSibling: boolean;
  placement: 'first' | 'after';
};

/**
 * Where the focus-view "+" places a new system element relative to `focusElement`.
 *
 * Uses {@link canNestSystemElement}. A parent that can host the element gets it
 * as the first child. A parent that cannot gets it as the next sibling, so
 * enrich does not drop the pre-generated id.
 */
export const focusViewSystemElementAddOptions = (
  focusElement: OneOfElements | undefined,
): FocusViewSystemElementAddOptions => {
  if (!canNestSystemElement(focusElement)) {
    return { isSibling: true, placement: 'after' };
  }
  return { isSibling: false, placement: 'first' };
};

export const isEqualElements = (
  a: OneOfChildElements,
  b: OneOfChildElements,
): boolean => {
  if (a?.kind !== b?.kind) {
    return false;
  }

  // ProductElement contains all information needed
  if (isProductElement(a) && isProductElement(b)) {
    return (
      isEqualExpressionValues(a.count, b.count) &&
      a.product_id === b.product_id &&
      a.unit === b.unit &&
      a.generic_product_id === b.generic_product_id
    );
  }
  if (isElement(a) && isElement(b)) {
    if (
      a.selectedQuantity !== b.selectedQuantity ||
      a.name !== b.name ||
      a.category_id !== b.category_id ||
      a.elements?.length !== b.elements?.length ||
      a.isDeactivated !== b.isDeactivated
    ) {
      return false;
    }

    const propsA = getElementAndQuantityProperties(a).filter(hasCount);
    const propsB = getElementAndQuantityProperties(b).filter(hasCount);

    if (!isElementPropertyListEqual(propsA, propsB)) {
      return false;
    }

    const childrenA = a.elements.filter(shouldCompareGeneratedProductElement);
    const childrenB = b.elements.filter(shouldCompareGeneratedProductElement);

    // Handle deeper structures
    for (let i = 0; i < childrenA.length; i++) {
      const childA = childrenA[i];
      const childB = childrenB[i];

      if (!childA || !childB || !isEqualElements(childA, childB)) {
        return false;
      }
    }

    return true;
  }
  return false;
};

const shouldCompareGeneratedProductElement = (element: OneOfChildElements) =>
  !isProductElement(element) || element.generic_product_id !== undefined;

/**
 * Test if the element is deactivated. Hidden elements and inactive element versions are also considered deactivated
 * @param element
 * @returns
 */
export const isDeactivated = (
  element: OneOfListElements | undefined,
  _countInactiveVersionsAsDeactivated = true,
): boolean => {
  if (isElement(element)) {
    if (element.isDeactivated === true) {
      return true;
    }
  }

  return false;
};

/**
 * True when {@link elementId} or any ancestor on the path from {@link version} is deactivated per {@link isDeactivated}.
 * Use with a resolved version in record mode so visibility matches proposal-applied state.
 *
 * Proposal visibility is baked into {@link IElement.isDeactivated} on the tree.
 */
export const isDeactivatedInElementTree = (
  version: Baseline,
  elementId: string,
): boolean => {
  const path = getPathToElement(version, elementId, false);
  const el = getElementById(version, elementId);
  if (!path || !el) return false;

  return [...path, el].some((node) => isDeactivated(node, false));
};

/**
 * If the element has been deleted from a resolved proposal view.
 * @param element
 * @returns
 */
export const isDeleted = (element: OneOfElements | undefined): boolean =>
  isElement(element) && !!element.isDeleted;

/**
 * Legacy EPD-picker element id left in old projects; stripped by migration and
 * excluded from pipeline/results. No longer created by the app.
 */
export const isTemporaryElementId = (id: string): boolean =>
  id === TMP_ELEMENT_ID;

export const elementHasChild = (element: IElement): boolean =>
  element.elements?.length > 0;

/**
 * Replace the product backing a {@link IProductElement} while preserving the original
 * generic product reference (so EPD swaps keep their boverket fallback) and keeping the
 * existing unit (all product elements are category/recipe-generated and own their unit).
 *
 * @param element - Existing product element
 * @param newProduct - Product to swap in
 * @returns Updated product element
 */
export const replaceProductInElement = (
  element: IProductElement,
  newProduct: IProduct,
): IProductElement => {
  const generic_product_id =
    element.generic_product_id !== newProduct.id
      ? (element.generic_product_id ?? element.product_id)
      : undefined;

  return {
    ...element,
    product_id: newProduct.id,
    generic_product_id,
    unit: element.unit,
  };
};

export const getRootElements = (version: Baseline): IElement[] => {
  return getChildElementsOfVersions(version).map((element) => {
    if (element.category_id && !element.fallbackName) {
      const category = getElementCategoryById(element.category_id);
      return { ...element, fallbackName: category.name };
    }
    return element;
  });
};

/**
 * Get all ids in element including children and their properties/quantities
 * @param searchIn - The root element to search in
 * @returns - An array of all ids
 */
export const getElementAndPropertyIds = (
  searchIn: OneOfSearchableElements,
): string[] =>
  flattenElements(searchIn)
    .flatMap((el) => [
      el.id,
      ...getElementAndQuantityProperties(el).map((p) => p.id),
    ])
    .filter(isDefined);

/**
 * Use this to also search by name (and other properties)
 * @param element
 * @param compareTo
 */
export const isElementMatch = <T extends OneOfElements>(
  element: T,
  idOrName: string | undefined | number,
): boolean => {
  if (idOrName === undefined) {
    return false;
  }
  if (element.id === idOrName) {
    return true;
  }
  return isNameMatch(getElementName(element), idOrName.toString());
};

/**
 * Return a function that can be used to check if an element matches a static id or name
 * @param idOrName
 * @returns
 */
export const getIsElementMatchFn =
  <T extends OneOfElements>(idOrName: string | undefined | number) =>
  (element: T) =>
    isElementMatch(element, idOrName);

/**
 * If the element can be expanded in ElementList or not
 * @param element
 */
export const isExpandableElement = (element: OneOfListElements): boolean =>
  (isElement(element) || isProjectFolder(element)) && hasChildren(element);
